'use client';

import { REGEXP_ONLY_DIGITS_AND_CHARS } from 'input-otp';
import { type ReactNode, useRef, useState } from 'react';
import { VariantProps } from 'tailwind-variants';

import { toastBase } from '@/ui-kit/toast';

import { InputOTP } from './components/InputOTP/InputOTP';
import { InputOTPGroup } from './components/InputOTPGroup/InputOTPGroup';
import { InputOTPSlot } from './components/InputOTPSlot/InputOTPSlot';
import { ResendCodeTimer } from './components/ResendCodeTimer/ResendCodeTimer';
import { SendCodeResponseType } from './types';
import { styles } from './UiKitInputOTP.styles';

type FormStatusType = {
  status: VariantProps<typeof styles>['status'];
  message?: string;
};

export interface UiKitInputOTPProps {
  /** Функция для отправки кода подтверждения. */
  onSendCode: (code: string) => Promise<SendCodeResponseType>;
  /** Функция для повторной отправки кода. */
  onResend: () => Promise<void> | void;
  /** Максимальная длина кода. Определяет количество символов, которые можно ввести. (по умолчанию 6 символов) */
  maxLength?: number;
  /**
   * Регулярное выражение для валидации ввода кода.
   * По умолчанию `REGEXP_ONLY_DIGITS_AND_CHARS` из `input-otp`.
   */
  pattern?: string;
  /** Показывать таймер повторной отправки. По умолчанию `true`. */
  showResend?: boolean;
  /** Таймаут повторной отправки в секундах. */
  resendTimeout?: number;
  /** Дополнительный контент под полем ввода (вместо или вместе с resend). */
  afterInput?: ReactNode;

  onComplete?: () => void;
}

export function UiKitInputOTP({
  onSendCode,
  onResend,
  onComplete,
  afterInput,
  maxLength = 6,
  pattern = REGEXP_ONLY_DIGITS_AND_CHARS,
  showResend = true,
  resendTimeout
}: UiKitInputOTPProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const isSubmittingRef = useRef(false);

  const [value, setValue] = useState('');
  const [formStatus, setFormStatus] = useState<FormStatusType>({
    status: 'default'
  });

  const slots = styles({ status: formStatus.status });

  const confirmNumber = async (code: string) => {
    if (code.length !== maxLength || isSubmittingRef.current) return;

    isSubmittingRef.current = true;
    inputRef.current?.blur();
    setFormStatus({ status: 'loading' });

    const { status: responseStatus, message, toastMessage } = await onSendCode(code);

    if (toastMessage) {
      toastBase(toastMessage, { status: responseStatus });
    }

    if (responseStatus === 'error') {
      isSubmittingRef.current = false;
      setFormStatus({ status: 'error', message });
      return;
    }

    setFormStatus({ status: 'completed', message });
    onComplete?.();
  };

  const handleChange = (nextValue: string) => {
    setValue(nextValue);

    if (nextValue.length === maxLength) {
      void confirmNumber(nextValue);
    }
  };

  return (
    <div className={slots.base()}>
      <InputOTP
        maxLength={maxLength}
        value={value}
        onChange={handleChange}
        pattern={pattern}
        ref={inputRef}
        onKeyDown={async (e) => {
          if (e.key === 'Enter') {
            await confirmNumber(value);
          }
        }}
      >
        <InputOTPGroup>
          {Array.from({ length: maxLength }).map((_, i) => (
            <InputOTPSlot
              key={i}
              index={i}
              className={slots.inputText()}
            />
          ))}
        </InputOTPGroup>
      </InputOTP>
      <span className={slots.helperText()}>{formStatus.message}</span>
      {showResend && (
        <ResendCodeTimer
          className={slots.resendText()}
          onResend={onResend}
          timeout={resendTimeout}
        />
      )}
      {afterInput}
      {formStatus.status === 'loading' && (
        <div className={slots.loadingContainer()}>
          <span className={slots.loading()} />
        </div>
      )}
    </div>
  );
}
